import React, { useId } from 'react';
import { GradientSvgDefs } from '../../lib/gradients';
import { dashArray } from '../../lib/fills';

/* ------------------------------------------------------------------ */
/* Shimmer                                                             */
/* ------------------------------------------------------------------ */

/** Sweep duration (s) — the highlight crosses the outline in the first ~55 % and rests for the remainder of each cycle. */
export const SHIMMER_DURATION = 3.2;

/**
 * Paint server for the shimmer highlight: a soft, slightly slanted white band that sweeps from left to right across a
 * `width` × `height` box (user units) and repeats. SMIL drives the sweep, so it runs anywhere the SVG is painted; the
 * layers that use it carry `className="wp-shimmer"`, which index.css hides for print, export hosts, thumbnails and
 * prefers-reduced-motion (the highlight is a screen effect — paper stays still).
 */
export const ShimmerDefs = ({ id, width, height }) => {
  const W = Math.max(1, width || 1);
  const band = W * 0.55;
  const slant = Math.min(band * 0.35, Math.max(1, height || 1));
  const rest = `${W + band * 0.15} 0`;
  return (
    <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={band} y2={slant}>
      <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
      <stop offset="0.35" stopColor="#ffffff" stopOpacity="0.45" />
      <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.9" />
      <stop offset="0.65" stopColor="#ffffff" stopOpacity="0.45" />
      <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
      <animateTransform attributeName="gradientTransform" type="translate" values={`${-band} 0; ${rest}; ${rest}`} keyTimes="0;0.55;1" dur={`${SHIMMER_DURATION}s`} repeatCount="indefinite" />
    </linearGradient>
  );
};

/* ------------------------------------------------------------------ */
/* Box outlines                                                        */
/* ------------------------------------------------------------------ */

/** Dashes scale with the weight (like the CSS dashed / dotted border the overlays replace). */
const scaledDash = (dashId, sw) => {
  const base = dashArray(dashId);
  if (!base) return undefined;
  if (dashId === 'dotted') return `0 ${sw * 2}`; // round dots one stroke-width apart
  if (dashId === 'square-dot') return `${sw} ${sw}`;
  return base.split(' ').map((v) => Math.max(0.5, Number(v) * Math.max(1, sw / 2))).join(' '); // 6 4 → 3 sw / 2 sw
};

/**
 * Gradient outline for a CSS-rendered box — rectangles / rounded rectangles / ellipses drawn as divs, text boxes and
 * picture frames. The box keeps a *transparent* CSS border of the same weight (so layout, padding and clipping do not
 * move) and this overlay paints the gradient stroke in the border zone, inside the box, exactly where the solid border
 * was. Everything is in on-screen pixels (`width` / `height` = box size × zoom, `strokeWidth` = weight × zoom), so the
 * gradient geometry matches `gradientToCss` for the same box and exports (the browser paints the DOM) match the canvas.
 * With `stroke.shimmer` a second, highlight-painted copy of the outline sweeps on top.
 */
export const OutlineSvg = ({ id, stroke, width, height, strokeWidth, radius = 0, ellipse = false, opacity = 1, testId = 'gradient-outline' }) => {
  const auto = useId().replace(/:/g, '');
  const base = id || `wp-ol-${auto}`;
  const paintId = `${base}-paint`;
  const shimmerId = `${base}-shimmer`;
  const W = Math.max(1, width || 1), H = Math.max(1, height || 1);
  const sw = Math.max(0.5, strokeWidth || 1);
  const half = sw / 2;
  const dashId = stroke?.dash || 'solid';
  const common = {
    fill: 'none',
    strokeWidth: sw,
    strokeDasharray: scaledDash(dashId, sw),
    strokeLinecap: dashId === 'dotted' ? 'round' : 'butt',
    strokeLinejoin: 'round',
  };
  const shape = (paint) => (ellipse
    ? <ellipse cx={W / 2} cy={H / 2} rx={Math.max(0, W / 2 - half)} ry={Math.max(0, H / 2 - half)} {...common} stroke={paint} />
    : <rect x={half} y={half} width={Math.max(0, W - sw)} height={Math.max(0, H - sw)} rx={Math.max(0, radius - half)} ry={Math.max(0, radius - half)} {...common} stroke={paint} />);
  const shimmer = !!stroke?.shimmer;
  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none', overflow: 'visible', opacity }}
      aria-hidden="true"
      data-testid={testId}
      data-gradient-type={stroke?.gradient?.type}
      data-shimmer={shimmer ? '1' : undefined}
    >
      <defs>
        <GradientSvgDefs id={paintId} gradient={stroke?.gradient} width={W} height={H} />
        {shimmer && <ShimmerDefs id={shimmerId} width={W} height={H} />}
      </defs>
      {shape(`url(#${paintId})`)}
      {shimmer && <g className="wp-shimmer">{shape(`url(#${shimmerId})`)}</g>}
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/* Table grids                                                         */
/* ------------------------------------------------------------------ */

/**
 * Gradient borders for a table: the outer frame plus the inner cell rules, painted as one path with one gradient across
 * the whole grid (so a grid can fade from one corner to the other). The cells keep transparent borders of the same
 * weight; `xs` / `ys` are the inner column / row boundaries in pixels, measured from the rendered table.
 */
export const TableBordersSvg = ({ id, stroke, width, height, strokeWidth, xs = [], ys = [], opacity = 1, testId = 'gradient-table-borders' }) => {
  const auto = useId().replace(/:/g, '');
  const base = id || `wp-tb-${auto}`;
  const paintId = `${base}-paint`;
  const shimmerId = `${base}-shimmer`;
  const W = Math.max(1, width || 1), H = Math.max(1, height || 1);
  const sw = Math.max(0.5, strokeWidth || 1);
  const half = sw / 2;
  const d = [
    `M${half},${half} H${W - half} V${H - half} H${half} Z`,
    ...xs.map((x) => `M${x},0 V${H}`),
    ...ys.map((y) => `M0,${y} H${W}`),
  ].join(' ');
  const shimmer = !!stroke?.shimmer;
  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none', overflow: 'visible', opacity }}
      aria-hidden="true"
      data-testid={testId}
      data-gradient-type={stroke?.gradient?.type}
      data-shimmer={shimmer ? '1' : undefined}
      data-rules={`${xs.length}x${ys.length}`}
    >
      <defs>
        <GradientSvgDefs id={paintId} gradient={stroke?.gradient} width={W} height={H} />
        {shimmer && <ShimmerDefs id={shimmerId} width={W} height={H} />}
      </defs>
      <path d={d} fill="none" stroke={`url(#${paintId})`} strokeWidth={sw} strokeLinecap="butt" strokeLinejoin="miter" />
      {shimmer && <g className="wp-shimmer"><path d={d} fill="none" stroke={`url(#${shimmerId})`} strokeWidth={sw} strokeLinecap="butt" strokeLinejoin="miter" /></g>}
    </svg>
  );
};
